import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Lock, RefreshCw, ExternalLink, Users, Gauge, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mp_admin_token";

const scoreColor = (s) => (s >= 75 ? "#22C55E" : s >= 50 ? "#FACC15" : "#EF4444");

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-secondary border-2 border-zinc-800 p-5 flex items-center gap-4">
    <div className="bg-brand/10 p-3"><Icon className="h-6 w-6 text-brand" /></div>
    <div>
      <div className="font-display text-3xl text-white leading-none">{value}</div>
      <div className="font-heading text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
    </div>
  </div>
);

export default function LeadsPage() {
  const initialToken =
    new URLSearchParams(window.location.search).get("k") ||
    localStorage.getItem(TOKEN_KEY) ||
    "";
  const [token, setToken] = useState(initialToken);
  const inputRef = useRef(null);
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchLeads = async (t) => {
    if (!t) { setErr("Enter the password."); return; }
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { "X-Admin-Token": t } });
      setLeads(data);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
    } catch (e) {
      if (e?.response?.status === 401) {
        setErr("Wrong password.");
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        setErr("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchLeads(token);
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setLeads([]);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-secondary border-2 border-zinc-700 p-8 hard-shadow">
          <div className="flex items-center gap-2 mb-6">
            <img src="/movers-pro-logo.webp" alt="Movers Pro Marketing" className="h-7 w-auto" />
            <span className="font-heading text-sm uppercase tracking-widest text-zinc-500 border-l border-zinc-700 pl-2">Leads</span>
          </div>
          <label className="block font-heading text-xs uppercase tracking-widest text-zinc-400 mb-2">Admin Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
            <input
              ref={inputRef}
              type="password"
              onKeyDown={(e) => e.key === "Enter" && fetchLeads(inputRef.current.value)}
              placeholder="Enter password"
              className="w-full h-12 bg-background border-2 border-zinc-700 pl-10 pr-4 font-body text-white focus:outline-none focus:border-brand"
              data-testid="admin-password-input"
            />
          </div>
          {err && <p className="text-fail font-body text-sm mt-2" data-testid="admin-error">{err}</p>}
          <button
            onClick={() => fetchLeads(inputRef.current?.value)}
            disabled={loading}
            className="w-full h-12 mt-5 bg-brand hover:bg-brand-hover text-white font-heading text-base uppercase tracking-wider transition-colors duration-150 disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {loading ? "Checking..." : "Enter"}
          </button>
        </div>
      </div>
    );
  }

  const avg = leads.length
    ? Math.round(leads.reduce((s, l) => s + (l.overall_score || 0), 0) / leads.filter((l) => l.overall_score != null).length || 0)
    : 0;
  const emailed = leads.filter((l) => l.email_status === "sent").length;

  return (
    <div className="min-h-screen">
      <header className="border-b-2 border-zinc-800 bg-background sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/movers-pro-logo.webp" alt="Movers Pro Marketing" className="h-7 w-auto" />
            <span className="font-heading text-sm uppercase tracking-widest text-zinc-500 border-l border-zinc-700 pl-2">Leads</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => fetchLeads(token)} className="inline-flex items-center gap-2 border-2 border-zinc-700 hover:border-brand text-white font-heading text-xs uppercase tracking-wider px-4 h-10 transition-colors duration-150" data-testid="refresh-leads-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} className="font-heading text-xs uppercase tracking-wider text-zinc-500 hover:text-white transition-colors duration-150" data-testid="logout-btn">Log out</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Stat icon={Users} label="Total Leads" value={leads.length} />
          <Stat icon={Gauge} label="Avg. Score" value={isNaN(avg) ? "—" : avg} />
          <Stat icon={Mail} label="Reports Emailed" value={emailed} />
        </div>

        <div className="border-2 border-zinc-800 bg-secondary overflow-x-auto" data-testid="leads-table">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b-2 border-zinc-800">
                {["Business", "Contact", "Location", "Score", "Report", "Email", "Date"].map((h) => (
                  <th key={h} className="text-left font-heading text-[10px] uppercase tracking-widest text-zinc-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center font-body text-zinc-500" data-testid="no-leads">No leads yet.</td></tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-zinc-800 hover:bg-background/50 transition-colors duration-150" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-heading text-sm uppercase tracking-wide text-white">{l.business_name}</div>
                    {l.category && <div className="font-body text-xs text-zinc-500">{l.category}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-body text-sm text-zinc-200">{l.name}</div>
                    <div className="font-body text-xs text-zinc-500">{l.email}</div>
                    {l.phone && <div className="font-body text-xs text-zinc-500">{l.phone}</div>}
                  </td>
                  <td className="px-4 py-3 font-body text-sm text-zinc-300">{l.city || "—"}</td>
                  <td className="px-4 py-3">
                    {l.overall_score != null ? (
                      <span className="font-display text-2xl leading-none" style={{ color: scoreColor(l.overall_score) }}>{l.overall_score}</span>
                    ) : (
                      <span className="font-heading text-xs uppercase text-zinc-600">{l.status}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {l.status === "done" ? (
                      <a href={`/r/${l.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-heading text-xs uppercase tracking-wider text-brand hover:underline" data-testid={`view-report-${l.id}`}>
                        View <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : <span className="text-zinc-600 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-heading text-[10px] uppercase tracking-wider px-2 py-1 ${l.email_status === "sent" ? "bg-pass/20 text-pass" : "bg-zinc-800 text-zinc-500"}`}>
                      {l.email_status || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-body text-xs text-zinc-500 whitespace-nowrap">
                    {l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
